import keyBy from "lodash/keyBy";
import times from "lodash/times";
import moment from "moment";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import SvgIcon from "../../../parts/SvgIcon";
import ToolTip from "../../../parts/tooltip-portal";
import { calculateDayColumnWidths } from "../../../utils/calendar-column-widths";
import CalendarInsightsSection from "../../athlete-portal/calendar/components/CalendarInsightsSection";
import { getCalendarWeeksInsightsRange } from "../../athlete-portal/programs/program-insights/utils/inputs/calendarInsights";
import CalendarTrendLegend from "./components/CalendarTrendLegend";

// Visibility-toggle config for the v2 control panel.
// Each entry maps a button label → the CSS class added to the week wrapper.
const HIDE_TOGGLES = [
  { key: "stats", label: "Stats", cls: "wk-hide-stats" },
  { key: "notes", label: "Notes", cls: "wk-hide-notes" },
  { key: "coachNotes", label: "Coach Notes", cls: "wk-hide-coach-notes" },
  { key: "prs", label: "PRs", cls: "wk-hide-prs" },
];

// The "notes" layer covers the trainee's own notes (day + exercise), so its
// label follows the coach's client/athlete terminology preference.
const getHideToggleLabel = ({ key, label }, t, referAsClients) => {
  if (key !== "notes") return label;
  return referAsClients
    ? t("calendar.calendarWeek.clientNotes", "Client Notes")
    : t("calendar.calendarWeek.athleteNotes", "Athlete Notes");
};

const SHOW_TOGGLES = [
  { key: "rpe", label: "RPE", cls: "wk-show-rpe" },
  { key: "1rm", label: "%1RM", cls: "wk-show-1rm" },
  { key: "e1rm", label: "e1RM", cls: "wk-show-e1rm" },
  { key: "vel", label: "Velocity", cls: "wk-show-vel" },
  { key: "program", label: "Program", cls: "wk-show-program" },
];

// Local-state class-list builder, shared by the single-week wrapper and
// MultiWeekView (which renders in a sibling branch outside that wrapper).
const buildToggleClassNames = (hiddenLayers, shownColumns) =>
  [
    ...Array.from(hiddenLayers).map(
      (k) => HIDE_TOGGLES.find((t) => t.key === k)?.cls
    ),
    ...Array.from(shownColumns).map(
      (k) => SHOW_TOGGLES.find((t) => t.key === k)?.cls
    ),
  ]
    .filter(Boolean)
    .join(" ");

const {
  DATE_FORMATS: { DATE_YEAR_FORMAT, DATE_DAY_FORMAT },
} = require("../../../constants");

const getDailyStatsByDate = (dailyStats) => {
  return keyBy(dailyStats, (e) =>
    moment
      .utc((e.utcDate && e.utcDate.toString()) || e.date)
      .format(DATE_DAY_FORMAT)
  );
};

class CalendarWeekComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      weeks: [props.activeWeek],
      dailyStatsByDate: getDailyStatsByDate(props.dailyStats),
      hiddenLayers: new Set(),
      shownColumns: new Set(SHOW_TOGGLES.map(({ key }) => key)),
      orientation: (props.calendarRange || 1) > 1 ? "vertical" : "horizontal",
      alignDays: true,
      modalDate: null,
      modalWeekStart: null,
    };

    this.toggleHideLayer = this.toggleHideLayer.bind(this);
    this.toggleShowColumn = this.toggleShowColumn.bind(this);
    this.setOrientation = this.setOrientation.bind(this);
    this.toggleAlignDays = this.toggleAlignDays.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { activeWeek, dailyStats, calendarRange } = this.props;

    // Week navigation is driven entirely by the top week arrows; each nav is a
    // full refresh that re-renders a single week. We no longer accumulate
    // previous/subsequent weeks into the list.
    if (!activeWeek.isSame(prevProps.activeWeek)) {
      this.setState({
        weeks: [activeWeek],
        dailyStatsByDate: getDailyStatsByDate(dailyStats),
      });
    }

    // The orientation toggle only applies to multi-week views. If the range
    // crosses the single/multi-week boundary without a full remount, reset
    // orientation to the correct default instead of leaving it stuck on
    // whichever state was active before the crossing.
    const wasMultiWeek = (prevProps.calendarRange || 1) > 1;
    const isMultiWeek = (calendarRange || 1) > 1;
    if (wasMultiWeek !== isMultiWeek) {
      this.setState({ orientation: "vertical", alignDays: true });
    }
  }

  openModal(date, weekStart) {
    this.setState({ modalDate: date, modalWeekStart: weekStart || null });
  }

  closeModal() {
    this.setState({ modalDate: null, modalWeekStart: null });
  }

  toggleHideLayer(key) {
    this.setState((prev) => {
      const next = new Set(prev.hiddenLayers);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { hiddenLayers: next };
    });
  }

  toggleShowColumn(key) {
    this.setState((prev) => {
      const next = new Set(prev.shownColumns);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { shownColumns: next };
    });
  }

  setOrientation(orientation) {
    this.setState({ orientation });
  }

  toggleAlignDays() {
    this.setState((prev) => ({ alignDays: !prev.alignDays }));
  }

  render() {
    const {
      activeWeek,
      endOfWeek,
      query,
      workoutNotes,
      workoutCoachNotes,
      WeekDay,
      activeExercises = [],
      athleteId,
      athlete,
      athleteFlexibleProgram,
      activeUser,
      activeUserToRequest,
      organizationOwner,
      clientConfiguration,
      fromMetric,
      withV2Controls,
      calendarRange: calendarRangeProp,
      MultiWeekView,
      WeekDayModal,
      scheduledSessions = {},
      scheduledSections = {},
    } = this.props;
    const calendarRange = calendarRangeProp || 1;
    // activeWeek is the start of the currently visible range, not "today". To
    // keep the range-switch buttons anchored on the same end date (rather than
    // compounding backwards on every click), compute that end date once here.
    const rangeEndWeek =
      calendarRange > 1
        ? activeWeek.clone().add(calendarRange - 1, "weeks")
        : activeWeek;
    // Edit mode mirrors the month view: a URL flag (query.editCalendar) so it
    // survives DayEditor's full-page-reload actions. v2 only, and capped at 8
    // weeks: each editable week mounts 7 DayEditors (one SortableJS instance
    // each), so 12w/24w stay read-only to keep the DOM/drag cost bounded.
    const editMode = Boolean(
      withV2Controls && query.editCalendar && calendarRange <= 8
    );
    const {
      weeks,
      dailyStatsByDate,
      hiddenLayers,
      shownColumns,
      orientation,
      alignDays,
      modalDate,
      modalWeekStart,
    } = this.state;
    const hasMultipleWeeks = weeks.length > 1;
    const conditionalStyle = hasMultipleWeeks ? { visibility: "hidden" } : {};
    const conditionalClass = hasMultipleWeeks ? " multiple-weeks" : "";
    const orientationClass =
      orientation === "horizontal" ? "wk-days-horizontal" : "";
    const toggleClassNames = buildToggleClassNames(hiddenLayers, shownColumns);
    // Trend indicators default to on; only an explicit `false` (set by the
    // toggle) turns them off, so an unset query param still renders as active.
    const relTrendsOn = query.relTrends !== false;
    const startingCalendarDay = activeUser?.startingCalendarDay || 0;
    // Insights span every visible week. In multi-week mode the visible weeks are
    // derived from calendarRange (matching MultiWeekView), not this.state.weeks
    // (which navigation keeps pinned to a single week).
    const insightsWeekStarts =
      calendarRange > 1
        ? times(calendarRange, (wi) => activeWeek.clone().add(wi * 7, "days"))
        : weeks;
    const weeksInsightsRange = getCalendarWeeksInsightsRange(
      insightsWeekStarts,
      startingCalendarDay
    );
    const insightsPeriodKeys =
      weeksInsightsRange?.visiblePeriods
        .map((period) => period.periodKey)
        .join("|") ?? "";
    const flexibleCycle = athleteFlexibleProgram?.[0];
    const calendarReturnHref = `/athlete-portal/calendar?view=week&week=${
      query.week || ""
    }&athlete=${athleteId}`;
    const flexibleProgramHref = `/athlete-portal/programs/flexible-program/${
      flexibleCycle?._id
    }?athlete=${athleteId}${
      query.organizationCoach
        ? `&organizationCoach=${query.organizationCoach}`
        : ""
    }&backTo=${encodeURIComponent(calendarReturnHref)}`;

    return (
      <main className="Calendar Calendar--week">
        <header className={`${conditionalClass}`}>
          <section>
            <hgroup>
              {withV2Controls && calendarRange <= 8 ? (
                <button
                  type="button"
                  className="bg-(--dark-grey) pr-2!"
                  style={conditionalStyle}
                  onClick={() =>
                    submit(`/actions/refresh/?`, {
                      method: "GET",
                      body: { editCalendar: !query.editCalendar },
                    })
                  }
                >
                  {this.props.t("calendar.calendarWeek.editMode")}
                  <input
                    type="checkbox"
                    name="editCalendar"
                    role="switch"
                    aria-checked={Boolean(query.editCalendar)}
                    checked={Boolean(query.editCalendar)}
                    readOnly
                  />
                  <ToolTip
                    body={this.props.t("calendar.calendarWeek.editModeTooltip")}
                  >
                    ?
                  </ToolTip>
                </button>
              ) : withV2Controls ? null : (
                <a
                  href={`/actions/refresh?week=${moment().format(
                    DATE_DAY_FORMAT
                  )}`}
                  style={conditionalStyle}
                  data-button-type="outline"
                  role="button"
                >
                  {this.props.t("calendar.calendarWeek.thisWeek")}
                </a>
              )}
            </hgroup>

            <hgroup>
              <div role="month-handler" style={conditionalStyle}>
                <div className="bg-(--dark-grey)!">
                  <a
                    data-button-type="text-white"
                    role="button"
                    data-icon-only
                    href={`/actions/refresh?week=${activeWeek
                      .clone()
                      .add(-1, "weeks")
                      .format(DATE_DAY_FORMAT)}`}
                  >
                    <SvgIcon src="/img/new-icons/arrow-left.svg" />
                  </a>
                  <span className="flex-shrink-0">
                    {activeWeek.format("MMM D")} -{" "}
                    {endOfWeek.format("MMM D, YYYY")}
                  </span>
                  <a
                    data-button-type="text-white"
                    role="button"
                    data-icon-only
                    href={`/actions/refresh?week=${activeWeek
                      .clone()
                      .add(1, "weeks")
                      .format(DATE_DAY_FORMAT)}`}
                  >
                    <SvgIcon src="/img/new-icons/arrow-right.svg" />
                  </a>
                </div>
              </div>
            </hgroup>

            <hgroup>
              {withV2Controls && (
                <button
                  type="button"
                  className="trend-toggle wk-trend-toggle"
                  role="switch"
                  aria-checked={relTrendsOn}
                  onClick={() =>
                    submit("/actions/refresh/?", {
                      method: "GET",
                      body: { relTrends: !relTrendsOn },
                    })
                  }
                >
                  <span className="trend-toggle-track">
                    <span className="trend-toggle-knob" />
                  </span>
                  {this.props.t("calendar.calendarWeek.relTrends")}
                </button>
              )}
              {withV2Controls && (
                <a
                  href={`/actions/refresh?week=${moment().format(
                    DATE_DAY_FORMAT
                  )}`}
                  style={conditionalStyle}
                  data-button-type="outline"
                  role="button"
                >
                  {this.props.t("calendar.calendarWeek.thisWeek")}
                </a>
              )}
              <ToggleLinksInput
                name="view"
                className="view-segmented"
                options={[
                  {
                    text: this.props.t("calendar.topBarSingleDay.month"),
                    value: "month",
                    href: `/actions/refresh?view=month&month=${
                      query.week
                        ? moment(query.week, DATE_DAY_FORMAT).format(
                            DATE_YEAR_FORMAT
                          )
                        : query.date
                          ? moment(query.date, DATE_DAY_FORMAT).format(
                              DATE_YEAR_FORMAT
                            )
                          : ""
                    }&week=&date=&start=&end=&relTrends=true`,
                  },
                  {
                    text: this.props.t("calendar.topBarSingleDay.week"),
                    value: "week",
                    href: `/actions/refresh?view=week&week=${
                      query.month
                        ? moment(query.month, DATE_YEAR_FORMAT).format(
                            DATE_DAY_FORMAT
                          )
                        : query.date
                          ? query.date
                          : ""
                    }&month=&date=&start=&end=&relTrends=true`,
                  },
                  {
                    text: this.props.t("calendar.topBarSingleDay.day"),
                    value: "day",
                    href: `/actions/refresh?view=day&date=${
                      query.month
                        ? moment(query.month, DATE_YEAR_FORMAT).format(
                            DATE_DAY_FORMAT
                          )
                        : query.week
                          ? query.week
                          : ""
                    }&month=&week=&start=&end=&relTrends=true`,
                  },
                ]}
              />
            </hgroup>
          </section>
          {flexibleCycle && (
            <div className="flexible-calendar-chip-row">
              <a className="flexible-calendar-chip" href={flexibleProgramHref}>
                <SvgIcon src="/img/new-icons/bolt.svg" useCurrentColor={true} />
                {this.props.t("calendar.calendarWeek.flexibleActive", {
                  name: athlete?.name,
                })}
                <SvgIcon
                  src="/img/new-icons/settings.svg"
                  useCurrentColor={true}
                />
              </a>
            </div>
          )}
        </header>

        {withV2Controls && (
          <div className="wk-ctrl-panel">
            <div className="wk-ctrl-row">
              <div className="wk-ctrl-group wk-ctrl-group--filters">
                {HIDE_TOGGLES.map((toggle) => (
                  <button
                    key={toggle.key}
                    type="button"
                    sz="small"
                    data-button-type={
                      !hiddenLayers.has(toggle.key) ? "outline" : "secondary"
                    }
                    onClick={() => this.toggleHideLayer(toggle.key)}
                  >
                    {!hiddenLayers.has(toggle.key) ? "✓ " : ""}
                    {getHideToggleLabel(
                      toggle,
                      this.props.t,
                      Boolean(activeUser?.referAsClients)
                    )}
                  </button>
                ))}
                <span className="wk-ctrl-sep">|</span>
                {SHOW_TOGGLES.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    sz="small"
                    data-button-type={
                      shownColumns.has(key) ? "outline" : "secondary"
                    }
                    onClick={() => this.toggleShowColumn(key)}
                  >
                    {shownColumns.has(key) ? "✓ " : ""}
                    {label}
                  </button>
                ))}
              </div>
              <div className="wk-ctrl-group">
                {calendarRange > 1 && (
                  <>
                    <div className="view-segmented wk-orientation-toggle">
                      <button
                        type="button"
                        className={
                          orientation === "horizontal" ? "active" : undefined
                        }
                        aria-pressed={orientation === "horizontal"}
                        aria-label={this.props.t(
                          "calendar.calendarWeek.orientationHorizontal",
                          "Horizontal"
                        )}
                        onClick={() => this.setOrientation("horizontal")}
                      >
                        <SvgIcon src="/img/new-icons/arrow-right-from-line.svg" />
                      </button>
                      <button
                        type="button"
                        className={
                          orientation === "vertical" ? "active" : undefined
                        }
                        aria-pressed={orientation === "vertical"}
                        aria-label={this.props.t(
                          "calendar.calendarWeek.orientationVertical",
                          "Vertical"
                        )}
                        onClick={() => this.setOrientation("vertical")}
                      >
                        <SvgIcon src="/img/new-icons/arrow-down-from-line.svg" />
                      </button>
                    </div>
                    {orientation === "vertical" && (
                      <button
                        type="button"
                        sz="small"
                        className="wk-align-toggle"
                        data-button-type={alignDays ? "outline" : "secondary"}
                        aria-pressed={alignDays}
                        onClick={this.toggleAlignDays}
                      >
                        {alignDays ? "✓ " : ""}
                        {this.props.t(
                          "calendar.calendarWeek.alignDays",
                          "Align Days"
                        )}
                      </button>
                    )}
                    <span className="wk-ctrl-sep">|</span>
                  </>
                )}
                {[
                  { label: "24w", range: 24 },
                  { label: "12w", range: 12 },
                  { label: "8w", range: 8 },
                  { label: "4w", range: 4 },
                  { label: "2w", range: 2 },
                  {
                    label: this.props.t("calendar.calendarWeek.week", "Week"),
                    range: 1,
                  },
                ].map(({ label, range }) => (
                  <a
                    key={range}
                    role="button"
                    sz="small"
                    data-button-type={
                      calendarRange === range ? "outline" : "secondary"
                    }
                    href={`/actions/refresh?week=${rangeEndWeek
                      .clone()
                      .subtract(range - 1, "weeks")
                      .format(DATE_DAY_FORMAT)}&range=${range}&start=&end=`}
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {withV2Controls && relTrendsOn && (
          <CalendarTrendLegend activeUser={activeUser} />
        )}

        {calendarRange > 1 && MultiWeekView ? (
          <CalendarInsightsSection
            scope="calendar-week"
            enabled={Boolean(weeksInsightsRange && athleteId)}
            from={weeksInsightsRange?.from}
            to={weeksInsightsRange?.to}
            periodKeys={insightsPeriodKeys}
            visiblePeriods={weeksInsightsRange?.visiblePeriods}
            activeExercises={activeExercises}
            athleteId={athleteId}
            startingCalendarDay={startingCalendarDay}
            activeUser={activeUser}
            activeUserToRequest={activeUserToRequest}
            organizationOwner={organizationOwner}
            clientConfiguration={clientConfiguration}
            fromMetric={fromMetric}
          >
            {(weeksScrollRef) => (
              <MultiWeekView
                scrollRef={weeksScrollRef}
                activeWeek={activeWeek}
                calendarRange={calendarRange}
                editMode={editMode}
                isTrendsOn={relTrendsOn}
                onDayClick={withV2Controls ? this.openModal : undefined}
                alignDays={alignDays && orientation === "vertical"}
                isHorizontal={orientation === "horizontal"}
                className={[
                  toggleClassNames,
                  orientationClass,
                  editMode ? "wk-edit-mode" : "",
                  relTrendsOn ? "wk-trends-on" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
            )}
          </CalendarInsightsSection>
        ) : (
          <CalendarInsightsSection
            scope="calendar-week"
            enabled={Boolean(weeksInsightsRange && athleteId)}
            from={weeksInsightsRange?.from}
            to={weeksInsightsRange?.to}
            periodKeys={insightsPeriodKeys}
            visiblePeriods={weeksInsightsRange?.visiblePeriods}
            activeExercises={activeExercises}
            athleteId={athleteId}
            startingCalendarDay={startingCalendarDay}
            activeUser={activeUser}
            activeUserToRequest={activeUserToRequest}
            organizationOwner={organizationOwner}
            clientConfiguration={clientConfiguration}
            fromMetric={fromMetric}
          >
            {(weeksScrollRef) => (
              <section
                ref={weeksScrollRef}
                className={[
                  "Calendar-Weeks",
                  conditionalClass,
                  editMode ? "wk-edit-mode" : "",
                  withV2Controls ? toggleClassNames : "",
                  relTrendsOn ? "wk-trends-on" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {weeks.map((week, idx) => {
                  return (
                    <article
                      className="Calendar-Week"
                      id={week.format("YYYYMMDD")}
                      key={week.format("YYYYMMDD")}
                    >
                      {(() => {
                        const hasWorkoutFlags = [];
                        const dayCards = times(7, (day) => {
                          const date = week.clone().add(day, "days");
                          const dayString = date.format(DATE_DAY_FORMAT);
                          let notes, coachNotes;
                          const dailyStats = dailyStatsByDate[dayString];
                          const exercises = scheduledSessions[dayString];
                          const sections = scheduledSections[dayString] || [];
                          hasWorkoutFlags.push(
                            Boolean(exercises?.length) ||
                              Boolean(sections.length)
                          );

                          if (workoutNotes.notes?.length) {
                            notes = workoutNotes.notes.find(
                              (note) =>
                                date.format("YYYY-MM-DD") ===
                                moment.utc(note.date).format("YYYY-MM-DD")
                            );
                          }
                          if (workoutCoachNotes?.notes?.length) {
                            coachNotes = workoutCoachNotes.notes.find(
                              (note) =>
                                date.format("YYYY-MM-DD") ===
                                moment.utc(note.date).format("YYYY-MM-DD")
                            );
                          }
                          return (
                            <WeekDay
                              key={`${idx}${day}`}
                              date={date}
                              notes={notes}
                              coachNotes={coachNotes}
                              dailyStats={dailyStats}
                              editMode={editMode}
                              onDayClick={
                                withV2Controls
                                  ? (d) => this.openModal(d, activeWeek)
                                  : undefined
                              }
                            />
                          );
                        });
                        // No edit-mode gate: month view's own edit-mode-only
                        // shrink was a limitation, not the intended pattern
                        // (product decision — shrink whenever the grid
                        // renders). Edit mode just uses the wider ambient
                        // "full" column value so DayEditor previews stay
                        // usable, matching .wk-edit-mode's own CSS width.
                        const columnWidths = withV2Controls
                          ? calculateDayColumnWidths(
                              hasWorkoutFlags,
                              editMode
                                ? {
                                    // Wider floor than read-only's 7.5rem: a
                                    // DayEditor's own "+ Add Exercise" button
                                    // needs more room than a bare REST card.
                                    // +32px over the DayEditor's base 150px:
                                    // on hover, "+ Add Exercise"/"Import
                                    // Workout"/"Add Workout Section" all
                                    // appear at once and were crowding each
                                    // other at 150px.
                                    fullColumn: "minmax(18rem, 1fr)",
                                    emptyColumn: "182px",
                                  }
                                : {
                                    // 15rem-27rem when trends are on (matches
                                    // .wk-trends-on .wk-day-grid's own floor
                                    // below — this inline style overrides
                                    // that class-based CSS whenever a row has
                                    // a mix of busy/rest days, so it has to
                                    // carry the same trends-aware range
                                    // itself rather than leaving it to CSS).
                                    // Capped at 27rem — same ceiling as the
                                    // trends-on card's own max-width (.wk-
                                    // trends-on .wk-col-card) — rather than
                                    // left as a flexible `1fr`: with few busy
                                    // columns on a wide viewport, an uncapped
                                    // `1fr` track kept growing well past what
                                    // the card actually renders, stranding
                                    // dead space and making the fixed-width
                                    // rest columns look tiny by contrast (see
                                    // the matching note on the multi-week
                                    // horizontal rule for the same fix).
                                    fullColumn: relTrendsOn
                                      ? "minmax(15rem, 27rem)"
                                      : "minmax(12rem, 1fr)",
                                    emptyColumn: "7.5rem",
                                  }
                            )
                          : null;
                        return withV2Controls ? (
                          <div
                            className={`wk-day-grid ${orientationClass}`.trim()}
                            style={
                              columnWidths
                                ? {
                                    gridTemplateColumns: columnWidths.join(" "),
                                  }
                                : undefined
                            }
                          >
                            {dayCards}
                          </div>
                        ) : (
                          dayCards
                        );
                      })()}
                    </article>
                  );
                })}
              </section>
            )}
          </CalendarInsightsSection>
        )}

        {modalDate && WeekDayModal && (
          <WeekDayModal
            date={modalDate}
            weekStart={modalWeekStart}
            onClose={this.closeModal}
          />
        )}
      </main>
    );
  }
}

export default withTranslation("dashboard")(CalendarWeekComponent);
